import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useContractSync,
  useDepositCheckout,
  useDsm3DPreview,
  useExtractBill,
  useFinanceConfig,
  useFinanceUrlClick,
  usePropertyMap,
  useProposalHtml,
  useRecommendQuote,
  useSolarBuildingInsights,
  useSubmitHelioQuote,
} from "@/api/hooks";
import { apiMutation } from "@/api/client";
import { useHelioScore } from "@/api/hooks/useHelioScorePreliminary";
import type { BillData, Project, PropertyMapResult, QuoteSessionEnrichment, QuoteTier, SolarBuildingInsightsResult } from "@/api/types";
import { useAuth } from "@/contexts/AuthContext";
import { useQuoteFlow, type PendingPlanContact } from "@/contexts/QuoteFlowContext";
import { API_BASE } from "@/constants/api";
import type { QuoteAccountMode } from "@/components/quote/QuoteAccountPanel";
import { clearRoofMeshLayoutCache } from "@/components/quote/RoofMesh3DViewer";
import {
  scoreBand,
  scrubPostcode,
  trackHelioScoreLeadSubmit,
  trackQuoteComplete,
  trackQuoteStart,
  trackQuoteStep,
} from "@/analytics";
import { looksLikeUprn, parseHandoffPrefill, recordCtaClick } from "@/hooks/useEnergyPlanPrefill";
import { confirmAddressKey, useQuoteSession } from "@/hooks/useQuoteSession";
import { meshGroundFrameFromStats, type MeshGroundFrame } from "@/utils/satelliteGroundPlane";
import { selectPlanOptions } from "@/utils/planOptionSelection";
import { PLAN_PANEL_DISPLAY_CAP, type RoofMeshLayout } from "@/utils/roofMeshPanelPlacement";
import { resolveAerialImageForSubmit, type SatellitePanelSnapshot } from "@/utils/satellitePanelOverlay";
import { buildFormPatchFromBillData, greetingNameFromBill, processBillUpload } from "@/utils/quoteBill";
import { peekPendingPlanDraft, takePendingPlanDraft, type PendingPlanDraft } from "@/utils/planHandoff";
import { clearPanelLayoutSession } from "@/utils/panelLayoutSession";
import { composeAddress, formToUkAddressParts, parseUkAddressParts, type UkAddressParts } from "@/utils/quoteMapper";
import { buildHelioScoreInputFromFunnel, type EpcEvidenceSlice } from "@/utils/helioScoreFunnel";
import { resolveAnnualSpendGbp } from "@/utils/doNothingProjection";
import {
  DEFAULT_FINANCE_APR,
  DEFAULT_FINANCE_TERM_MONTHS,
  amortisedMonthly,
  buildFinanceApplyUrl,
} from "@/utils/financePayment";
import {
  confidenceFromProject,
  sizedCatalog,
  snapCatalogOption,
  tierKeyFor,
  uniqueBatteryKwh,
  uniquePanelCounts,
  type CatalogOption,
  type ConfidenceStage,
} from "@/utils/planViewModel";
import { toast } from "sonner";

export interface PlanFormState {
  billFile: File | null;
  billFileUrl: string | null;
  billFileKey: string | null;
  billData: BillData | null;
  annualKwh: number;
  energySupplier: string;
  address1: string;
  address2: string;
  city: string;
  region: string;
  address: string;
  postcode: string;
  uprn: string;
  udprn: string;
  satelliteImageUrl: string | null;
  mapCoordinates: { latitude: number; longitude: number } | null;
  satelliteZoom: number | null;
  satelliteImageSize: string | null;
  formattedAddress: string | null;
  propertyMapError: string | null;
  solarInsights: PropertyMapResult["solarInsights"] | null;
  solarBuildingInsights: SolarBuildingInsightsResult | null;
  mesh3dUrl: string | null;
  mesh3dFor: string | null;
  meshGroundFrame: MeshGroundFrame | null;
  selectedTier: "good" | "better" | "best" | null;
  tiers: { good: QuoteTier; better: QuoteTier; best: QuoteTier } | null;
  optionCatalog: QuoteTier[] | null;
  solarData: { roofAreaM2: number; irradianceKwhM2: number; panelCapacityW: number } | null;
  paymentType: "upfront" | "finance";
  customerName: string;
  customerEmail: string;
  customerPhone: string;
}

const STRING_FIELDS = [
  "energySupplier",
  "address1",
  "address2",
  "city",
  "region",
  "address",
  "postcode",
  "customerName",
  "customerEmail",
  "customerPhone",
] as const satisfies ReadonlyArray<keyof PlanFormState>;

function normalizeFormPatch(patch: Partial<PlanFormState>): Partial<PlanFormState> {
  const normalized = { ...patch };
  for (const key of STRING_FIELDS) {
    if (key in normalized && normalized[key] == null) {
      normalized[key] = "";
    }
  }
  return normalized;
}

function contactFieldsFromPending(contact: PendingPlanContact | null): Partial<PlanFormState> {
  if (!contact) return {};
  return {
    ...(contact.customerName ? { customerName: contact.customerName } : {}),
    ...(contact.customerEmail ? { customerEmail: contact.customerEmail } : {}),
    ...(contact.customerPhone ? { customerPhone: contact.customerPhone } : {}),
  };
}

function mapEnrichment(enrichment: QuoteSessionEnrichment | null): EpcEvidenceSlice | null {
  if (!enrichment) return null;
  return {
    epc_rating: enrichment.epc_rating,
    epc_floor_area_m2: enrichment.epc_floor_area_m2,
    property_tenure: enrichment.property_tenure,
    property_built_form: enrichment.property_built_form,
    property_value_mid_gbp: enrichment.property_value_mid_gbp,
    epc_data_source: enrichment.epc_data_source,
    price_paid_data_source: enrichment.price_paid_data_source,
    evidence_flags: enrichment.evidence_flags,
  };
}

function isValidPhone(phone: string): boolean {
  return /^[\d\s+()-]{7,}$/.test(phone.trim());
}

function formFromDraft(draft: PendingPlanDraft | null, postcode: string): PlanFormState {
  const base = emptyForm(draft?.address.postcode?.toUpperCase() || postcode);
  if (!draft) return base;
  const address = draft.address;
  return {
    ...base,
    address1: address.address1 ?? "",
    address2: address.address2 ?? "",
    city: address.city ?? "",
    region: address.region ?? "",
    postcode: (address.postcode ?? "").toUpperCase(),
    uprn: address.uprn ?? "",
    udprn: address.udprn ?? "",
    address: composeAddress(address),
    annualKwh: draft.annualKwh > 0 ? draft.annualKwh : base.annualKwh,
    customerName: draft.customerName ?? "",
    customerEmail: draft.customerEmail ?? "",
    customerPhone: draft.customerPhone ?? "",
  };
}

function emptyForm(postcode: string): PlanFormState {
  return {
    billFile: null,
    billFileUrl: null,
    billFileKey: null,
    billData: null,
    annualKwh: 3500,
    energySupplier: "",
    address1: "",
    address2: "",
    city: "",
    region: "",
    address: "",
    postcode,
    uprn: "",
    udprn: "",
    satelliteImageUrl: null,
    mapCoordinates: null,
    satelliteZoom: null,
    satelliteImageSize: null,
    formattedAddress: null,
    propertyMapError: null,
    solarInsights: null,
    solarBuildingInsights: null,
    mesh3dUrl: null,
    mesh3dFor: null,
    meshGroundFrame: null,
    selectedTier: null,
    tiers: null,
    optionCatalog: null,
    solarData: null,
    paymentType: "finance",
    customerName: "",
    customerEmail: "",
    customerPhone: "",
  };
}

export function useCustomerPlan() {
  const search = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const entryParam = search.get("entry");
  const entryFromBill = entryParam === "bill";
  const initialPostcode = search.get("postcode")?.toUpperCase() ?? "";
  const widgetPropertyId = search.get("property_id") || "";
  const widgetPartnerId = search.get("partner_id") || "";
  const widgetAgencyName = search.get("agency_name") || "";
  const widgetHandoffToken = search.get("token") || "";

  const {
    claimPendingBillFile,
    claimPendingBillData,
    claimPendingAddress,
    claimPendingEnrichment,
    claimPendingWidgetContext,
    claimPendingPlanContact,
    hasPendingAddress,
    hasPendingBillFile,
    wasPendingUploadStarted,
    markPendingUploadStarted,
  } = useQuoteFlow();
  const { login, register, syncContactPhone, isAuthenticated, user, token } = useAuth();
  const session = useQuoteSession();

  const [sessionChecked, setSessionChecked] = useState(false);
  const pendingDraftRef = useRef(peekPendingPlanDraft());
  const funnelEvChoice = useRef<boolean | null>(pendingDraftRef.current ? pendingDraftRef.current.includesEv : null);
  const [form, setForm] = useState<PlanFormState>(() => formFromDraft(pendingDraftRef.current, initialPostcode));
  const [tariffNote, setTariffNote] = useState<string | null>(null);
  const [epcEnrichment, setEpcEnrichment] = useState<EpcEvidenceSlice | null>(null);
  const [usageTouched, setUsageTouched] = useState(false);
  const [selectedOption, setSelectedOption] = useState<QuoteTier | null>(null);
  const [customised, setCustomised] = useState(false);
  const [includesEv, setIncludesEv] = useState(() => pendingDraftRef.current?.includesEv ?? false);
  const [heatPump, setHeatPump] = useState(() => pendingDraftRef.current?.includesHeatPump ?? false);
  const [financeTermMonths, setFinanceTermMonths] = useState(DEFAULT_FINANCE_TERM_MONTHS);
  const [roofLayout, setRoofLayout] = useState<RoofMeshLayout | null>(null);
  const [layoutRevision, setLayoutRevision] = useState(0);
  const [roofCapacity, setRoofCapacity] = useState<number | null>(null);
  const satellitePlacementRef = useRef<SatellitePanelSnapshot | null>(null);
  const rememberSatellitePlacement = useCallback((snapshot: SatellitePanelSnapshot | null) => {
    satellitePlacementRef.current = snapshot;
  }, []);
  const [recommendError, setRecommendError] = useState<string | null>(null);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [clearingMapCache, setClearingMapCache] = useState(false);
  const [roofMediaPending, setRoofMediaPending] = useState(false);
  const [accountMode, setAccountMode] = useState<QuoteAccountMode>("register");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedLead, setSubmittedLead] = useState<Project | null>(null);
  const [contractSigningUrl, setContractSigningUrl] = useState<string | null>(null);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [contractLoading, setContractLoading] = useState(false);

  const extractBill = useExtractBill();
  const recommend = useRecommendQuote();
  const propertyMap = usePropertyMap();
  const solarInsightsQuery = useSolarBuildingInsights();
  const dsm3DPreview = useDsm3DPreview();
  const submitQuote = useSubmitHelioQuote();
  const { data: financeConfig } = useFinanceConfig();
  const financeUrlClick = useFinanceUrlClick();
  const createDeposit = useDepositCheckout();
  const contractSync = useContractSync();
  const { data: proposalHtml, isLoading: proposalHtmlLoading, isError: proposalHtmlError } = useProposalHtml(submittedLead?.id);

  const formRef = useRef(form);
  formRef.current = form;
  const customisedRef = useRef(customised);
  customisedRef.current = customised;
  const lastPropertyMapKey = useRef<string | null>(null);
  const propertyMapRequestId = useRef(0);
  const enrichmentKeyRef = useRef<string | null>(null);
  const meshCacheRef = useRef({ url: null as string | null, forAddress: null as string | null });
  meshCacheRef.current = { url: form.mesh3dUrl, forAddress: form.mesh3dFor };
  const lastRecommendKey = useRef<string | null>(null);
  const signedInEmailRef = useRef<string | undefined>(user?.email);
  signedInEmailRef.current = user?.email;

  const update = useCallback((patch: Partial<PlanFormState>) => {
    setForm((current) => ({ ...current, ...normalizeFormPatch(patch) }));
  }, []);

  const updateAddressFields = useCallback((patch: Partial<UkAddressParts>) => {
    setForm((current) => {
      const next = {
        ...current,
        ...normalizeFormPatch(patch as Partial<PlanFormState>),
        ...(patch.uprn !== undefined ? { uprn: patch.uprn ?? "" } : {}),
        ...(patch.udprn !== undefined ? { udprn: patch.udprn ?? "" } : {}),
      };
      next.address = composeAddress(next);
      return next;
    });
  }, []);

  const confirmAddressEnrichment = useCallback(async (fields: UkAddressParts) => {
    const key = confirmAddressKey(fields);
    if (enrichmentKeyRef.current === key) return;
    enrichmentKeyRef.current = key;
    try {
      const enrichment = await session.confirmAddress(fields, {
        lastStep: "analysis",
        data: { postcode: fields.postcode, address1: fields.address1 },
        propertyId: widgetPropertyId || undefined,
        partnerId: widgetPartnerId || undefined,
        agencyName: widgetAgencyName || undefined,
      });
      if (enrichmentKeyRef.current !== key) return;
      setEpcEnrichment(mapEnrichment(enrichment));
    } catch {
      if (enrichmentKeyRef.current === key) {
        enrichmentKeyRef.current = null;
        setEpcEnrichment(null);
      }
    }
  }, [session, widgetAgencyName, widgetPartnerId, widgetPropertyId]);

  const fetchPropertyMap = useCallback(async (
    address: string,
    postcode: string,
    uprn?: string,
    options?: { force?: boolean },
  ) => {
    const trimmedAddress = address.trim();
    const trimmedPostcode = postcode.trim();
    if (!trimmedAddress || !trimmedPostcode) return;
    const mapKey = `${trimmedAddress}|${trimmedPostcode}`;
    if (!options?.force && lastPropertyMapKey.current === mapKey) return;

    const requestId = ++propertyMapRequestId.current;
    lastPropertyMapKey.current = mapKey;
    const stillCurrent = () => propertyMapRequestId.current === requestId;
    update({ propertyMapError: null });
    setRoofMediaPending(true);
    try {
      const result = await propertyMap.mutateAsync({ address: trimmedAddress, postcode: trimmedPostcode });
      if (!stillCurrent()) return;
      update({
        satelliteImageUrl: result.satelliteImage?.url ?? null,
        mapCoordinates: result.coordinates ?? null,
        satelliteZoom: result.satelliteImage?.zoomLevel ?? 20,
        satelliteImageSize: result.satelliteImage?.imageSize ?? "600x400",
        formattedAddress: result.formattedAddress ?? null,
        solarInsights: result.solarInsights ?? null,
        propertyMapError: result.satelliteImage?.url ? null : "Satellite image unavailable for this address.",
      });
      if (!options?.force && meshCacheRef.current.url && meshCacheRef.current.forAddress === mapKey) return;
      if (!result.coordinates) return;
      try {
        const solarData = await solarInsightsQuery.mutateAsync({
          latitude: result.coordinates.latitude,
          longitude: result.coordinates.longitude,
          include_data_layers: true,
          uprn: uprn || undefined,
          address: trimmedAddress,
          postcode: trimmedPostcode,
          satellite_image_url: result.satelliteImage?.url ?? undefined,
          map_zoom: result.satelliteImage?.zoomLevel ?? 20,
          map_size: result.satelliteImage?.imageSize ?? "600x400",
        });
        if (!stillCurrent()) return;
        update({ solarBuildingInsights: solarData });
        if (solarData?.inspire_coverage !== "unavailable" && solarData?.google_solar?.dsm_url) {
          try {
            const mesh3d = await dsm3DPreview.mutateAsync({
              dsm_url: solarData.google_solar.dsm_url,
              rgb_url: solarData.google_solar.rgb_url,
              mask_url: solarData.google_solar.mask_url,
              title_geojson: solarData.property_boundary_geojson,
              panels: solarData.google_solar.solar_panels,
              include_panels: false,
              z_scale: 1.0,
              output_formats: ["glb"],
              buffer_meters: 0.0,
              refresh: options?.force === true,
            });
            if (!stillCurrent()) return;
            if (mesh3d?.mesh_glb_path_url) {
              update({
                mesh3dUrl: mesh3d.mesh_glb_path_url,
                mesh3dFor: mapKey,
                meshGroundFrame: meshGroundFrameFromStats(mesh3d.stats?.ground_frame),
              });
            }
          } catch (error) {
            console.error("Failed to generate 3D mesh:", error);
          }
        }
      } catch (error) {
        console.error("Failed to fetch solar building insights:", error);
      }
    } catch {
      if (!stillCurrent()) return;
      lastPropertyMapKey.current = null;
      update({
        satelliteImageUrl: null,
        mapCoordinates: null,
        satelliteZoom: null,
        satelliteImageSize: null,
        solarInsights: null,
        propertyMapError: "Could not load satellite view. You can still continue with your address.",
      });
    } finally {
      if (stillCurrent()) setRoofMediaPending(false);
    }
  // Mutation objects are new every render. mutateAsync is stable, so the map effect does not re-arm mid-request.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dsm3DPreview.mutateAsync, propertyMap.mutateAsync, solarInsightsQuery.mutateAsync, update]);

  const catalog = useMemo(() => sizedCatalog(form.optionCatalog), [form.optionCatalog]);

  const recommendedOption = useMemo(() => {
    if (!form.tiers) return null;
    const capacity = roofCapacity ?? roofLayout?.panels.length ?? null;
    const sizingTarget = capacity != null ? Math.min(capacity, PLAN_PANEL_DISPLAY_CAP) : null;
    const selectable = catalog.filter(
      (option): option is CatalogOption & { strategyKey: string } => typeof option.strategyKey === "string",
    );
    if (sizingTarget != null && selectable.length > 0) {
      const picked = selectPlanOptions(selectable, sizingTarget);
      if (picked) return { ...picked.good, tier: "good" as const };
    }
    return form.tiers.good;
  }, [catalog, form.tiers, roofCapacity, roofLayout?.panels.length]);

  useEffect(() => {
    if (customisedRef.current || !recommendedOption) return;
    setSelectedOption(recommendedOption);
    if (funnelEvChoice.current == null) setIncludesEv(recommendedOption.includesEv);
    update({ selectedTier: tierKeyFor(recommendedOption) });
  }, [recommendedOption, update]);

  const applyOption = useCallback((option: QuoteTier, markCustom: boolean) => {
    setSelectedOption(option);
    setCustomised(markCustom);
    update({ selectedTier: tierKeyFor(option) });
  }, [update]);

  const panelCounts = uniquePanelCounts(catalog);
  const batteryChoices = uniqueBatteryKwh(
    selectedOption?.panels != null
      ? catalog.filter((option) => option.panels === selectedOption.panels)
      : catalog,
  );

  const setPanels = useCallback((panels: number) => {
    const option = snapCatalogOption(catalog, panels, selectedOption?.batteryKwh);
    if (option) applyOption(option, true);
  }, [applyOption, catalog, selectedOption?.batteryKwh]);

  const setBattery = useCallback((batteryKwh: number) => {
    const panels = selectedOption?.panels ?? catalog[0]?.panels;
    if (panels == null) return;
    const option = snapCatalogOption(catalog, panels, batteryKwh);
    if (option) applyOption(option, true);
  }, [applyOption, catalog, selectedOption?.panels]);

  const resetToRecommended = useCallback(() => {
    if (!recommendedOption) return;
    clearPanelLayoutSession();
    setLayoutRevision((revision) => revision + 1);
    setCustomised(false);
    setSelectedOption(recommendedOption);
    setIncludesEv(recommendedOption.includesEv);
    update({ selectedTier: "good" });
  }, [recommendedOption, update]);

  const annualSpend = resolveAnnualSpendGbp({
    annualSpendGbp: form.billData?.annual_spend_gbp,
    annualKwh: form.annualKwh,
    tariffPencePerKwh: form.billData?.current_tariff_pence_per_kwh,
  });

  const helioScoreInput = buildHelioScoreInputFromFunnel(
    {
      postcode: form.postcode,
      address: form.address,
      annualKwh: form.annualKwh,
      billData: form.billData,
      usageTouched,
      solarInsights: form.solarInsights ?? null,
      solarData: form.solarData,
      selectedTier: selectedOption
        ? { ...selectedOption, includesEv }
        : null,
      epcEnrichment,
    },
    { step: selectedOption ? 2 : 1 },
  );
  const { data: helioScore, isLoading: helioScoreLoading } = useHelioScore(helioScoreInput);

  const runRecommend = useCallback(async () => {
    const current = formRef.current;
    const addressFields = formToUkAddressParts(current);
    if (!addressFields) return;
    const spend = resolveAnnualSpendGbp({
      annualSpendGbp: current.billData?.annual_spend_gbp,
      annualKwh: current.annualKwh,
      tariffPencePerKwh: current.billData?.current_tariff_pence_per_kwh,
    });
    setIsGeneratingPlan(true);
    setRecommendError(null);
    try {
      const [, result] = await Promise.all([
        confirmAddressEnrichment(addressFields),
        recommend.mutateAsync({
          annualKwh: current.annualKwh,
          postcode: current.postcode,
          address: current.address,
          ...(spend != null && spend > 0 ? { currentSpendGbp: spend } : {}),
          ...(current.billData?.current_tariff_pence_per_kwh
            ? { importTariffPence: current.billData.current_tariff_pence_per_kwh, tariffFromBill: true }
            : {}),
          ...(current.solarInsights ? { solarInsights: current.solarInsights } : {}),
        }),
      ]);
      const { solarData, catalog: nextCatalog, tariff, ...tiers } = result;
      setTariffNote(tariff?.note ?? null);
      if (!tiers?.good || !tiers?.better || !tiers?.best) {
        setRecommendError("We could not build a system for this property.");
        return;
      }
      update({
        tiers: tiers as PlanFormState["tiers"],
        optionCatalog: Array.isArray(nextCatalog) ? nextCatalog : [],
        solarData: solarData ?? null,
        selectedTier: current.selectedTier ?? "good",
      });
    } catch {
      setRecommendError("We could not build a system for this property.");
    } finally {
      setIsGeneratingPlan(false);
    }
  }, [confirmAddressEnrichment, recommend, update]);

  const recommendKey = [
    form.postcode.trim(),
    form.address.trim(),
    form.annualKwh,
    form.billData?.current_tariff_pence_per_kwh ?? "",
    form.solarInsights?.azimuthDegrees ?? "",
    form.solarInsights?.roofAreaM2 ?? "",
    form.solarInsights?.shadingFactor ?? "",
  ].join("|");

  useEffect(() => {
    if (!sessionChecked || submittedLead) return;
    if (!form.address.trim() || !form.postcode.trim() || extractBill.isPending) return;
    if (lastRecommendKey.current === recommendKey) return;
    const timer = window.setTimeout(() => {
      lastRecommendKey.current = recommendKey;
      void runRecommend();
    }, 700);
    return () => window.clearTimeout(timer);
  }, [extractBill.isPending, form.address, form.postcode, recommendKey, runRecommend, sessionChecked, submittedLead]);

  const retryRecommend = useCallback(() => {
    lastRecommendKey.current = null;
    void runRecommend();
  }, [runRecommend]);

  useEffect(() => {
    trackQuoteStart(initialPostcode || undefined);
    trackQuoteStep("your_plan");
  }, [initialPostcode]);

  useEffect(() => {
    if (!user?.email) return;
    setForm((current) => (current.customerEmail === user.email ? current : { ...current, customerEmail: user.email }));
  }, [user?.email]);

  useEffect(() => {
    if (!user?.name) return;
    setForm((current) => (current.customerName ? current : { ...current, customerName: user.name }));
  }, [user?.name]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // A fresh home handoff already seeded the form. Restoring here would
        // replace that address with an older saved session.
        if (
          entryFromBill
          || widgetHandoffToken
          || hasPendingBillFile()
          || hasPendingAddress()
          || peekPendingPlanDraft()
          || wasPendingUploadStarted()
        ) {
          return;
        }
        const id = session.getSessionId();
        if (!id) return;
        const result = await session.fetchSession(id);
        if (cancelled || !result || "expired" in result) {
          if (!cancelled && result && "expired" in result) toast.message("Your saved progress expired — starting fresh.");
          return;
        }
        if (result.enrichment) setEpcEnrichment(mapEnrichment(result.enrichment));
        const saved = result.data as {
          form?: Partial<PlanFormState>;
          includesEv?: boolean;
          includesHeatPump?: boolean;
        };
        if (typeof saved.includesEv === "boolean") {
          funnelEvChoice.current = saved.includesEv;
          setIncludesEv(saved.includesEv);
        }
        if (typeof saved.includesHeatPump === "boolean") setHeatPump(saved.includesHeatPump);
        if (saved.form) {
          const patch = normalizeFormPatch(saved.form);
          if (signedInEmailRef.current) patch.customerEmail = signedInEmailRef.current;
          if (!patch.address1 && patch.address) {
            const parts = parseUkAddressParts([patch.address, patch.postcode].filter(Boolean).join(", "));
            patch.address1 = parts.address1;
            patch.address2 = parts.address2;
            patch.city = parts.city;
            patch.region = parts.region;
            if (parts.postcode) patch.postcode = parts.postcode;
            patch.address = composeAddress(parts);
          } else if (patch.address1 || patch.city || patch.region || patch.address2) {
            patch.address = composeAddress({
              address1: patch.address1 ?? "",
              address2: patch.address2 ?? "",
              city: patch.city ?? "",
              region: patch.region ?? "",
            });
          }
          update(patch);
        }
      } finally {
        if (!cancelled) setSessionChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  // Mount-only restore. Later edits must not be overwritten by the saved session.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sessionChecked) return;
    const timer = window.setTimeout(() => {
      const current = formRef.current;
      if (!current.postcode) return;
      void session.persist("your_plan", {
        includesEv,
        includesHeatPump: heatPump,
        form: {
          annualKwh: current.annualKwh,
          energySupplier: current.energySupplier,
          address1: current.address1,
          address2: current.address2,
          city: current.city,
          region: current.region,
          address: current.address,
          postcode: current.postcode,
          satelliteImageUrl: current.satelliteImageUrl,
          mapCoordinates: current.mapCoordinates,
          formattedAddress: current.formattedAddress,
          mesh3dUrl: current.mesh3dUrl,
          mesh3dFor: current.mesh3dFor,
          meshGroundFrame: current.meshGroundFrame,
          selectedTier: current.selectedTier,
          paymentType: current.paymentType,
          customerName: current.customerName,
          customerEmail: current.customerEmail,
          customerPhone: current.customerPhone,
        },
      });
    }, 500);
    return () => window.clearTimeout(timer);
  }, [form.address, form.annualKwh, form.mesh3dUrl, form.paymentType, form.postcode, form.selectedTier, heatPump, includesEv, session, sessionChecked]);

  useEffect(() => {
    setRoofLayout(null);
    setRoofCapacity(null);
  }, [form.mesh3dUrl]);

  useEffect(() => {
    const address = form.address.trim();
    const postcode = form.postcode.trim();
    if (!address || !postcode) return;
    const mapKey = `${address}|${postcode}`;
    if (lastPropertyMapKey.current === mapKey) return;
    if (form.mesh3dFor && form.mesh3dFor !== mapKey) {
      update({ mesh3dUrl: null, mesh3dFor: null, meshGroundFrame: null });
    }
    const timer = window.setTimeout(() => {
      void fetchPropertyMap(address, postcode, form.uprn || undefined);
    }, 500);
    return () => window.clearTimeout(timer);
  }, [fetchPropertyMap, form.address, form.mesh3dFor, form.postcode, form.uprn, update]);

  const runBillUpload = useCallback(async (file: File) => {
    await processBillUpload(file, extractBill.mutateAsync, update);
    lastRecommendKey.current = null;
  }, [extractBill.mutateAsync, update]);

  const pendingHandled = useRef(false);
  useEffect(() => {
    takePendingPlanDraft();
    if (pendingHandled.current) return;
    const pendingContact = claimPendingPlanContact();
    const contactFields = contactFieldsFromPending(pendingContact);
    const pendingAddress = claimPendingAddress();
    const pendingWidget = claimPendingWidgetContext();
    if (wasPendingUploadStarted() && !pendingAddress) {
      pendingHandled.current = true;
      if (Object.keys(contactFields).length > 0) update(contactFields);
      return;
    }
    const pendingBill = claimPendingBillFile();
    if (pendingBill && !pendingAddress) {
      pendingHandled.current = true;
      markPendingUploadStarted();
      const pendingBillData = claimPendingBillData();
      update({
        billFile: pendingBill,
        ...contactFields,
        ...(pendingBillData ? buildFormPatchFromBillData(pendingBillData) : {}),
      });
      if (pendingBillData) lastRecommendKey.current = null;
      else void runBillUpload(pendingBill);
      return;
    }
    if (pendingAddress) {
      pendingHandled.current = true;
      const fields = { ...pendingAddress };
      const propertyId = pendingWidget?.propertyId || widgetPropertyId;
      if (!fields.uprn && looksLikeUprn(propertyId)) fields.uprn = propertyId;
      updateAddressFields(fields);
      if (Object.keys(contactFields).length > 0) update(contactFields);
      const pendingEnrichment = claimPendingEnrichment(fields);
      if (pendingEnrichment) {
        enrichmentKeyRef.current = confirmAddressKey(fields);
        setEpcEnrichment(mapEnrichment(pendingEnrichment));
      } else {
        void confirmAddressEnrichment(fields);
      }
      return;
    }
    if (Object.keys(contactFields).length > 0) {
      pendingHandled.current = true;
      update(contactFields);
    }
  }, [claimPendingAddress, claimPendingBillData, claimPendingBillFile, claimPendingEnrichment, claimPendingPlanContact, claimPendingWidgetContext, confirmAddressEnrichment, markPendingUploadStarted, runBillUpload, update, updateAddressFields, wasPendingUploadStarted, widgetPropertyId]);

  const widgetHandled = useRef(false);
  useEffect(() => {
    if (widgetHandled.current || !widgetHandoffToken) return;
    widgetHandled.current = true;
    void (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/widget/handoff?token=${encodeURIComponent(widgetHandoffToken)}`);
        const body = await res.json();
        if (!res.ok || !body.success) {
          toast.error(body.error || "This energy plan link is invalid or has already been used.");
          return;
        }
        const prefill = parseHandoffPrefill(body);
        if (prefill.address) {
          updateAddressFields(prefill.address);
          void confirmAddressEnrichment(prefill.address);
        }
        void recordCtaClick(widgetHandoffToken);
      } catch {
        toast.error("Unable to load your property details.");
      }
    })();
  }, [confirmAddressEnrichment, updateAddressFields, widgetHandoffToken]);

  const setAnnualKwh = useCallback((annualKwh: number) => {
    setUsageTouched(true);
    lastRecommendKey.current = null;
    update({ annualKwh });
  }, [update]);

  const setPaymentType = useCallback((paymentType: "upfront" | "finance") => {
    update({ paymentType });
  }, [update]);

  const financeMonthly = selectedOption
    ? amortisedMonthly(selectedOption.price, DEFAULT_FINANCE_APR, financeTermMonths)
    : 0;

  const handleSubmit = useCallback(async () => {
    const current = formRef.current;
    const option = selectedOption;
    if (!option || !current.customerName.trim() || !current.customerEmail.trim()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      let authToken = token ?? localStorage.getItem("auth_token");
      if (!authToken) {
        const email = current.customerEmail.trim();
        if (accountMode === "login") {
          if (!password) {
            setSubmitError("Please enter your password.");
            return;
          }
          const authResult = await login(email, password);
          if (!authResult.success) {
            setSubmitError(authResult.message || "Sign in failed.");
            return;
          }
        } else {
          if (password.length < 8) {
            setSubmitError("Password must be at least 8 characters.");
            return;
          }
          if (password !== passwordConfirmation) {
            setSubmitError("Passwords do not match.");
            return;
          }
          const authResult = await register(current.customerName.trim() || "Customer", email, password, passwordConfirmation);
          if (!authResult.success) {
            const message = authResult.message || "Could not create your account.";
            if (message.toLowerCase().includes("email") && message.toLowerCase().includes("taken")) {
              setAccountMode("login");
              setSubmitError("An account with this email already exists. Please sign in.");
            } else {
              setSubmitError(message);
            }
            return;
          }
        }
        authToken = localStorage.getItem("auth_token");
        if (!authToken) {
          setSubmitError("Signed in but no session token was returned. Please try again.");
          return;
        }
      }
      if (isValidPhone(current.customerPhone)) {
        const syncResult = await syncContactPhone(current.customerPhone.trim());
        if (!syncResult.success) toast.error(syncResult.message || "Could not save your phone number.");
      }
      const panelCountForAerial = option.panels ?? null;
      const aerialImage = await resolveAerialImageForSubmit(
        () => satellitePlacementRef.current,
        {
          mesh3dUrl: current.mesh3dUrl,
          meshGroundFrame: current.meshGroundFrame,
          satelliteImageUrl: current.satelliteImageUrl,
          propertyBoundary: current.solarBuildingInsights?.property_boundary_geojson ?? null,
          visiblePanelCount: panelCountForAerial,
        },
      );
      const lead = await submitQuote.mutateAsync({
        customerName: current.customerName,
        customerEmail: current.customerEmail,
        customerPhone: current.customerPhone,
        address: current.address,
        postcode: current.postcode,
        annualKwh: current.annualKwh,
        currentTariff: (current.billData?.current_tariff_pence_per_kwh ?? 28.62) / 100,
        energySupplier: current.energySupplier,
        billFileKey: current.billFileKey ?? undefined,
        selectedTier: tierKeyFor(option),
        ...(typeof option.systemOptionId === "number" ? { systemOptionId: option.systemOptionId } : {}),
        packageLabel: option.label,
        packageDescription: option.description || undefined,
        systemSizeKw: option.systemKw,
        batteryKwh: option.batteryKwh,
        includesEv,
        totalPrice: option.price,
        financeMonthly: current.paymentType === "finance"
          ? amortisedMonthly(option.price, DEFAULT_FINANCE_APR, financeTermMonths)
          : amortisedMonthly(option.price, DEFAULT_FINANCE_APR, DEFAULT_FINANCE_TERM_MONTHS),
        financeTermMonths: current.paymentType === "finance" ? financeTermMonths : DEFAULT_FINANCE_TERM_MONTHS,
        paymentType: current.paymentType,
        annualSavings: option.annualBenefitY1 ?? option.annualSavings,
        paybackYears: option.paybackYears,
        co2OffsetTonnes: option.co2Tonnes,
        solarInsights: current.solarInsights ?? undefined,
        ...(aerialImage ? { aerialImage } : {}),
      });
      setSubmittedLead(lead);
      trackQuoteComplete(String(lead?.id ?? "unknown"));
      trackHelioScoreLeadSubmit({
        scoreBand: scoreBand(helioScore?.overall_score ?? 0),
        leadSource: "quote_funnel",
        quoteId: lead?.id ? String(lead.id) : undefined,
        postcodeArea: scrubPostcode(current.postcode),
      });
      void session.clearSession();
    } catch {
      toast.error("Failed to submit quote. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    accountMode,
    financeTermMonths,
    helioScore?.overall_score,
    includesEv,
    login,
    password,
    passwordConfirmation,
    register,
    selectedOption,
    session,
    submitQuote,
    syncContactPhone,
    token,
  ]);

  const startContractSigning = useCallback(async (quoteId: number) => {
    setContractLoading(true);
    try {
      const result = await apiMutation<{ success: boolean; signing_url?: string; message?: string }>(
        `/api/quotes/${quoteId}/contract-ready`,
      );
      if (result.signing_url) {
        setContractSigningUrl(result.signing_url);
        setContractModalOpen(true);
      } else {
        toast.error(result.message ?? "Could not start contract signing.");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not start contract signing.");
    } finally {
      setContractLoading(false);
    }
  }, []);

  const handleContractReady = useCallback(async () => {
    if (!submittedLead?.id) return;
    if (!submittedLead.customerAvailableDate) {
      setAvailabilityOpen(true);
      return;
    }
    await startContractSigning(submittedLead.id);
  }, [startContractSigning, submittedLead]);

  const handleContractCompleted = useCallback(async () => {
    if (!submittedLead?.id) return;
    try {
      const result = await contractSync.mutateAsync(submittedLead.id);
      setSubmittedLead((prev) => prev
        ? {
            ...prev,
            contractSignedAt: result.contractSignedAt ?? new Date().toISOString(),
            lifecycleStatus: result.lifecycleStatus ?? prev.lifecycleStatus,
          }
        : prev);
      toast.success("Agreement signed — you can now pay your deposit.");
    } catch {
      setSubmittedLead((prev) => (prev ? { ...prev, contractSignedAt: new Date().toISOString() } : prev));
      toast.success("Agreement signed. You can now pay your deposit.");
    } finally {
      setContractModalOpen(false);
      setContractSigningUrl(null);
    }
  }, [contractSync, submittedLead?.id]);

  const rememberAvailability = useCallback((date: string) => {
    setSubmittedLead((prev) => (prev ? { ...prev, customerAvailableDate: date } : prev));
    setAvailabilityOpen(false);
  }, []);

  const handlePayDeposit = useCallback(() => {
    if (!submittedLead?.id) return;
    createDeposit.mutate(
      { projectId: submittedLead.projectId ?? submittedLead.id, origin: window.location.origin },
      {
        onSuccess: (data) => {
          if (data?.url) {
            const opened = window.open(data.url, "_blank", "noopener,noreferrer");
            if (!opened) window.location.href = data.url;
          }
        },
        onError: () => toast.error("Could not start deposit checkout"),
      },
    );
  }, [createDeposit, submittedLead]);

  const handleApplyForFinance = useCallback(async () => {
    if (!submittedLead?.id) return;
    const loanAmount = selectedOption?.price ?? submittedLead.totalPrice ?? submittedLead.lead?.totalPrice ?? 0;
    const termMonths = financeTermMonths || submittedLead.financeTermMonths || DEFAULT_FINANCE_TERM_MONTHS;
    const fallbackApplyUrl = buildFinanceApplyUrl(
      financeConfig?.applyUrl ?? submittedLead.financeUrl ?? submittedLead.lead?.financeUrl ?? "",
      loanAmount,
      termMonths,
    );
    try {
      const clickResult = await financeUrlClick.mutateAsync(submittedLead.id);
      const applyUrl = clickResult.applyUrl
        ? buildFinanceApplyUrl(clickResult.applyUrl, loanAmount, termMonths)
        : fallbackApplyUrl;
      window.open(applyUrl, "_blank", "noopener,noreferrer");
      toast.success("Finance application opened.");
    } catch {
      window.open(fallbackApplyUrl, "_blank", "noopener,noreferrer");
    }
  }, [financeConfig?.applyUrl, financeTermMonths, financeUrlClick, selectedOption?.price, submittedLead]);

  const clearMapCache = useCallback(async () => {
    const current = formRef.current;
    if (!current.address.trim() || !current.postcode.trim() || clearingMapCache) return;
    clearPanelLayoutSession();
    setLayoutRevision((revision) => revision + 1);
    clearRoofMeshLayoutCache();
    lastPropertyMapKey.current = null;
    meshCacheRef.current = { url: null, forAddress: null };
    update({ mesh3dUrl: null, mesh3dFor: null, meshGroundFrame: null });
    setClearingMapCache(true);
    try {
      await fetchPropertyMap(current.address, current.postcode, current.uprn || undefined, { force: true });
    } finally {
      setClearingMapCache(false);
    }
  }, [clearingMapCache, fetchPropertyMap, update]);

  const hasProperty = Boolean(
    entryFromBill
    || form.postcode.trim()
    || form.address.trim()
    || form.address1.trim()
    || extractBill.isPending
    || form.billFile,
  );
  const phase: "checking" | "example" | "building" | "ready" | "submitted" = submittedLead
    ? "submitted"
    : !sessionChecked
      ? "checking"
      : hasProperty
        ? selectedOption
          ? "ready"
          : "building"
        : "example";

  const confidence: ConfidenceStage = confidenceFromProject(submittedLead);

  return {
    phase,
    form,
    update,
    updateAddressFields,
    greetingName: greetingNameFromBill(form.billData?.customer_name || form.customerName || user?.name),
    addressLine: form.formattedAddress || [form.address, form.postcode].filter(Boolean).join(", ") || null,
    tariffNote,
    selectedOption,
    panelCounts,
    batteryChoices,
    setPanels,
    setBattery,
    resetToRecommended,
    customised,
    includesEv,
    setIncludesEv,
    heatPump,
    setHeatPump,
    setAnnualKwh,
    helioScore,
    helioScoreLoading,
    epcRating: epcEnrichment?.epc_rating ?? null,
    upliftGbp: helioScore?.financial_uplift?.property_value_uplift_gbp ?? null,
    annualSpend,
    financeTermMonths,
    setFinanceTermMonths,
    financeMonthly,
    financeDisclosure: financeConfig?.disclosureText ?? null,
    setPaymentType,
    isGeneratingPlan,
    recommendError,
    retryRecommend,
    extractPending: extractBill.isPending,
    runBillUpload,
    confirmAddressEnrichment,
    accountMode,
    setAccountMode,
    password,
    setPassword,
    passwordConfirmation,
    setPasswordConfirmation,
    submitError,
    isSubmitting,
    isAuthenticated,
    userEmail: user?.email,
    handleSubmit,
    submittedLead,
    proposalHtml,
    proposalHtmlLoading,
    proposalHtmlError,
    contractSigningUrl,
    contractModalOpen,
    setContractModalOpen,
    setContractSigningUrl,
    availabilityOpen,
    setAvailabilityOpen,
    rememberAvailability,
    contractLoading,
    depositPending: createDeposit.isPending,
    handleContractReady,
    handleContractCompleted,
    handlePayDeposit,
    handleApplyForFinance,
    setRoofLayout,
    setRoofCapacity,
    layoutRevision,
    rememberSatellitePlacement,
    clearMapCache,
    clearingMapCache,
    roofMediaPending,
    confidence,
  };
}

export type { CatalogOption };
